import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type {
  InfirmierPlanning,
  InterventionPlanning,
  TypeSoin,
} from '../../lib/api/types'
import { formatDate, formatDuree, formatHeure } from '../../lib/formatters'
import { creneaux } from '../../lib/temps'
import { Bouton, Champ, Etiquette, Liste, Pastille } from '../ui/Base'
import { Modale } from '../ui/Modale'
import { SelecteurSoins } from './SelecteurSoins'

interface Proprietes {
  intervention: InterventionPlanning | null
  infirmiers: InfirmierPlanning[]
  typesSoins: TypeSoin[]
  amplitude: { heure_ouverture: string; heure_fermeture: string; pas_minutes: number }
  /** Lecture seule pour un remplaçant sur le planning global (R6). */
  modifiable: boolean
  enCours: boolean
  onFermer: () => void
  onDeplacer: (date: string, heure: string, utilisateurId: string | null) => void
  onChangerSoins: (soins: string[]) => void
}

const LIBELLES_CONFLIT: Record<string, string> = {
  CHEVAUCHEMENT: 'Chevauchement',
  TRAJET_IMPOSSIBLE: 'Trajet trop court',
  HORS_DISPONIBILITE: 'Hors disponibilité',
}

/**
 * Détail d'une intervention, et **alternative accessible au glisser-déposer**.
 *
 * Le glisser-déposer natif est inutilisable au clavier et invisible pour un
 * lecteur d'écran. Ce formulaire « Déplacer vers… » fait exactement la même
 * mutation, avec la même gestion du 409 — c'est aussi la seule voie praticable
 * sur tablette, et une infirmière qui consulte son planning debout n'a pas de
 * souris.
 */
export function PanneauIntervention({
  intervention,
  infirmiers,
  typesSoins,
  amplitude,
  modifiable,
  enCours,
  onFermer,
  onDeplacer,
  onChangerSoins,
}: Proprietes) {
  const [date, setDate] = useState('')
  const [heure, setHeure] = useState('')
  const [infirmierId, setInfirmierId] = useState<string>('')

  useEffect(() => {
    if (!intervention) return
    setHeure(intervention.heure_debut)
    setInfirmierId(intervention.utilisateur_id ?? '')
  }, [intervention])

  if (!intervention) return null

  const infirmier = infirmiers.find((i) => i.id === intervention.utilisateur_id)
  const heuresPossibles = creneaux(
    amplitude.heure_ouverture,
    amplitude.heure_fermeture,
    amplitude.pas_minutes,
  )

  return (
    <Modale
      ouverte
      titre={`${intervention.patient_prenom} ${intervention.patient_nom}`}
      onFermer={onFermer}
    >
      <div className="space-y-4">
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Horaire</dt>
            <dd className="font-medium text-slate-900">
              {formatHeure(intervention.heure_debut)} – {formatHeure(intervention.heure_fin)}
              <span className="ml-1 text-slate-500">
                ({formatDuree(intervention.duree_minutes)})
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Infirmier</dt>
            <dd className="flex items-center gap-1.5 font-medium text-slate-900">
              {infirmier ? (
                <>
                  <Pastille couleur={infirmier.couleur} />
                  {infirmier.prenom} {infirmier.nom}
                </>
              ) : (
                <Etiquette ton="attention">Non affectée</Etiquette>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Statut</dt>
            <dd className="font-medium text-slate-900">{intervention.statut}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500">Ville</dt>
            <dd className="font-medium text-slate-900">{intervention.patient_ville}</dd>
          </div>
        </dl>

        {intervention.conflits.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {intervention.conflits.map((c) => (
              <Etiquette key={c} ton={c === 'CHEVAUCHEMENT' ? 'danger' : 'attention'}>
                {LIBELLES_CONFLIT[c] ?? c}
              </Etiquette>
            ))}
          </div>
        ) : null}

        <div>
          <p className="mb-1 text-sm font-medium text-slate-700">Soins</p>
          <SelecteurSoins
            typesSoins={typesSoins}
            selection={intervention.soins.map((s) => s.type_soin_id)}
            onValider={onChangerSoins}
            disabled={!modifiable || enCours}
          />
        </div>

        {modifiable ? (
          <form
            className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
            onSubmit={(e) => {
              e.preventDefault()
              onDeplacer(date || '', heure, infirmierId === '' ? null : infirmierId)
            }}
          >
            <p className="text-sm font-medium text-slate-700">Déplacer vers…</p>
            <div className="grid grid-cols-2 gap-3">
              <Champ libelle="Date" id="deplacer-date">
                <input
                  id="deplacer-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
              </Champ>
              <Champ libelle="Heure" id="deplacer-heure">
                <Liste id="deplacer-heure" value={heure} onChange={(e) => setHeure(e.target.value)}>
                  {heuresPossibles.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </Liste>
              </Champ>
            </div>
            <Champ libelle="Infirmier" id="deplacer-infirmier">
              <Liste
                id="deplacer-infirmier"
                value={infirmierId}
                onChange={(e) => setInfirmierId(e.target.value)}
              >
                <option value="">— Non affectée —</option>
                {infirmiers.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.prenom} {i.nom}
                  </option>
                ))}
              </Liste>
            </Champ>
            <Bouton type="submit" disabled={enCours} className="w-full">
              {enCours ? 'Déplacement…' : 'Déplacer'}
            </Bouton>
          </form>
        ) : (
          <p className="rounded-lg bg-slate-100 px-3 py-2 text-xs text-slate-600">
            Votre rôle permet la consultation du planning, pas sa modification.
          </p>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <Link
            to={`/patients/${intervention.patient_id}`}
            className="text-sm text-sky-700 hover:underline"
          >
            Voir la fiche patient
          </Link>
          <Bouton variante="secondaire" onClick={onFermer}>
            Fermer
          </Bouton>
        </div>

        <p className="text-[11px] text-slate-400">
          Dernière modification : {formatDate(intervention.updated_at)}
        </p>
      </div>
    </Modale>
  )
}
